import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Divider,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ModeButtons from "../Home/components/ModeButtons";
import PageCloseButton from "../shared/PageCloseButton";
import { managementPageShellSx } from "../shared/managementPageLayout";
import { desktopMainColumnInsetSx } from "../../layout/layoutMetrics";
import DynamicSearchBar from "../Users/CreateNewUser/components/DynamicSearchbar";
import GlobalRoleDetails from "./GlobalRoleDetails";
import {
  createGlobalRole,
  getAllGlobalRoles,
  getGlobalRoleById,
  updateGlobalRole,
} from "../../core/services/globalRole.service";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    color: "#f5f5f5",
    fontSize: 14,
    height: 46,
    borderRadius: 2,
    background: "rgba(255, 255, 255, 0.04)",
    transition: "all 0.3s ease",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.15)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.4)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(245, 245, 245, 0.8)",
      borderWidth: "1.5px",
    },
    "&.Mui-focused": {
      background: "rgba(255, 255, 255, 0.08)",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: 13,
    "&.Mui-focused": {
      color: "#f5f5f5",
    },
  },
};

const actionButtonSx = {
  color: "white",
  borderColor: "rgba(255,255,255,0.3)",
  fontSize: 11,
  px: 1.2,
};

const getRoleKey = (role, index) => {
  const id =
    role?.roleId ??
    role?.globalRolesId ??
    role?.GlobalRolesId ??
    role?.globalRoleId ??
    role?.GlobalRoleId ??
    null;
  return id ?? role.roleName ?? `role-${index}`;
};

export default function GlobalRolePage({ embedded = false }) {
  const [tabIndex, setTabIndex] = useState(1);
  const [roles, setRoles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleName, setRoleName] = useState("");
  const [activeRoleAction, setActiveRoleAction] = useState(null);
  const [roleDetails, setRoleDetails] = useState(null);
  const [editForm, setEditForm] = useState({ roleName: "" });
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updatingRole, setUpdatingRole] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const filteredRoles = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return roles;

    return roles.filter((role) => {
      const name = String(role.roleName ?? "").toLowerCase();
      const level = String(role.level ?? "").toLowerCase();
      const id = String(role.roleId ?? "").toLowerCase();
      return name.includes(term) || level.includes(term) || id.includes(term);
    });
  }, [roles, searchTerm]);

  const selectedRoleFromList = useMemo(() => {
    if (!activeRoleAction?.roleId) return null;

    return (
      roles.find(
        (role, index) =>
          String(getRoleKey(role, index)) === String(activeRoleAction.roleId),
      ) ?? null
    );
  }, [activeRoleAction, roles]);

  const roleForDetails = roleDetails ?? selectedRoleFromList;

  useEffect(() => {
    if (!activeRoleAction?.roleId) {
      setRoleDetails(null);
      return undefined;
    }

    const roleId =
      activeRoleAction?.roleApiId ??
      selectedRoleFromList?.roleId ??
      selectedRoleFromList?.globalRolesId ??
      selectedRoleFromList?.globalRoleId;

    if (!roleId) {
      setLoadingDetails(false);
      setSnackbar({
        open: true,
        message: "Role id is missing for this global role",
        severity: "error",
      });
      return undefined;
    }

    let cancelled = false;
    setLoadingDetails(true);

    getGlobalRoleById(roleId)
      .then((data) => {
        if (!cancelled) setRoleDetails(data);
      })
      .catch((error) => {
        if (!cancelled) {
          setRoleDetails(null);
          setSnackbar({
            open: true,
            message: error?.message || "Failed to load global role details",
            severity: "error",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDetails(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeRoleAction?.roleId, activeRoleAction?.roleApiId, activeRoleAction?.mode, selectedRoleFromList?.roleId]);

  useEffect(() => {
    if (activeRoleAction?.mode !== "edit" || !roleForDetails) return;

    setEditForm({
      roleName: roleForDetails.roleName ?? "",
    });
  }, [activeRoleAction?.mode, activeRoleAction?.roleId, roleForDetails]);

  const loadRoles = useCallback(async () => {
    setLoadingRoles(true);

    try {
      const list = await getAllGlobalRoles();
      setRoles(list);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || "Failed to load global roles",
        severity: "error",
      });
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  useEffect(() => {
    if (tabIndex === 1) {
      loadRoles();
    }
  }, [loadRoles, tabIndex]);

  const handleCreateRole = async (event) => {
    event.preventDefault();

    if (!roleName.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter role name",
        severity: "warning",
      });
      return;
    }

    setCreating(true);

    try {
      await createGlobalRole({ roleName: roleName.trim(), level: 1 });
      setRoleName("");
      setTabIndex(1);
      setSnackbar({
        open: true,
        message: "Global role created successfully",
        severity: "success",
      });
      await loadRoles();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || "Failed to create global role",
        severity: "error",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleRoleAction = (role, roleKey, mode) => {
    setActiveRoleAction({
      roleId: roleKey,
      roleApiId:
        role?.roleId ??
        role?.globalRolesId ??
        role?.GlobalRolesId ??
        role?.globalRoleId ??
        role?.GlobalRoleId ??
        null,
      mode,
    });
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdateRole = async (event) => {
    event.preventDefault();

    const roleId =
      roleForDetails?.roleId ??
      activeRoleAction?.roleApiId ??
      selectedRoleFromList?.roleId;
    const trimmedName = String(editForm.roleName ?? "").trim();

    if (!roleId) {
      setSnackbar({
        open: true,
        message: "Role id is missing for this global role",
        severity: "warning",
      });
      return;
    }

    if (!trimmedName) {
      setSnackbar({
        open: true,
        message: "Please enter role name",
        severity: "warning",
      });
      return;
    }

    setUpdatingRole(true);

    try {
      await updateGlobalRole({
        roleId,
        roleName: trimmedName,
        level: roleForDetails?.level ?? 1,
      });
      setSnackbar({
        open: true,
        message: "Global role updated successfully",
        severity: "success",
      });
      await loadRoles();
      const refreshed = await getGlobalRoleById(roleId);
      setRoleDetails(refreshed);
      setActiveRoleAction({ roleId: activeRoleAction?.roleId, roleApiId: roleId, mode: "view" });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || "Failed to update global role",
        severity: "error",
      });
    } finally {
      setUpdatingRole(false);
    }
  };

  const roleContent = (
    <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              mb: 1.5,
            }}
          >
            <BadgeOutlinedIcon
              sx={{ mr: 1, color: "white", fontSize: { xs: 20, sm: 22 } }}
            />
            <Typography variant="h6" fontWeight={600} fontSize={{ xs: 16, sm: 18 }}>
              Global Level Role Management
            </Typography>
            <PageCloseButton sx={managementPageShellSx.headerCloseButton} />
          </Box>

          <Tabs
            value={tabIndex}
            onChange={(_, value) => setTabIndex(value)}
            variant="fullWidth"
            textColor="inherit"
            sx={{
              mb: 1.5,
              minHeight: { xs: 40, sm: 36 },
              "& .MuiTab-root": {
                color: "white",
                fontSize: { xs: 11, sm: 12 },
                minHeight: { xs: 40, sm: 36 },
                px: { xs: 0.5, sm: 1 },
              },
            }}
          >
            <Tab
              value={1}
              icon={<BadgeOutlinedIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />}
              label="View Global Level Role"
            />
            <Tab
              value={0}
              icon={
                <AddCircleOutlineOutlinedIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
              }
              label="Create Global Level Role"
            />
          </Tabs>

          {tabIndex === 0 && (
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 550,
                mx: "auto",
                mt: 2.5,
                p: 2.5,
                borderRadius: 4,
                display: "flex",
                flexDirection: "column",
                background:
                  "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                color: "white",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <AddCircleOutlineOutlinedIcon
                  sx={{ mr: 1, fontSize: 22, color: "#facc15" }}
                />
                <Typography
                  fontWeight={600}
                  fontSize={17}
                  letterSpacing={0.5}
                  sx={{ textTransform: "uppercase" }}
                >
                  Create Global Level Role
                </Typography>
              </Box>

              <Divider sx={{ mb: 3, borderColor: "rgba(255,255,255,0.1)" }} />

              <Stack component="form" spacing={2.5} onSubmit={handleCreateRole}>
                <TextField
                  fullWidth
                  label="Role Name"
                  value={roleName}
                  onChange={(event) => setRoleName(event.target.value)}
                  placeholder="Enter role name"
                  sx={fieldSx}
                />

                <Button
                  type="submit"
                  variant="contained"
                  disabled={creating || !roleName.trim()}
                  sx={{
                    width: "180px",
                    mt: 1,
                    alignSelf: "center",
                    py: 1.2,
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: 2,
                    background: "#4f8a3d",
                    boxShadow: "0 4px 14px 0 rgba(79, 138, 61, 0.4)",
                    "&:hover": {
                      background: "#3f6f31",
                      boxShadow: "0 6px 20px 0 rgba(79, 138, 61, 0.5)",
                    },
                    "&.Mui-disabled": {
                      background: "rgba(255,255,255,0.16)",
                      color: "rgba(255,255,255,0.45)",
                      boxShadow: "none",
                    },
                  }}
                >
                  {creating ? (
                    <CircularProgress size={22} sx={{ color: "white" }} />
                  ) : (
                    "Create Global Role"
                  )}
                </Button>
              </Stack>
            </Paper>
          )}

          {tabIndex === 1 && (
            <Box sx={{ p: 1.5 }}>
              <DynamicSearchBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                placeholder="Search global roles..."
              />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    maxHeight: { xs: "none", md: "56vh" },
                    overflowY: { xs: "visible", md: "auto" },
                    pr: { xs: 0, md: 1 },
                    "&::-webkit-scrollbar": {
                      width: "5px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "rgba(255,255,255,0.3)",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "rgba(255,255,255,0.5)",
                    },
                    scrollbarWidth: "thin",
                    scrollbarColor: "rgba(255,255,255,0.3) transparent",
                  }}
                >
                  {loadingRoles ? (
                    <Box display="flex" justifyContent="center" minHeight="28vh">
                      <CircularProgress size={26} sx={{ color: "white" }} />
                    </Box>
                  ) : filteredRoles.length === 0 ? (
                    <Typography
                      align="center"
                      fontSize={13}
                      sx={{ color: "rgba(255,255,255,0.6)", py: 4 }}
                    >
                      {roles.length === 0
                        ? "No global roles found."
                        : "No global roles match your search."}
                    </Typography>
                  ) : (
                    filteredRoles.map((role, index) => {
                      const roleKey = getRoleKey(role, index);
                      const isSelected =
                        String(activeRoleAction?.roleId) === String(roleKey);
                      const isViewActive =
                        activeRoleAction?.mode === "view" && isSelected;
                      const isEditActive =
                        activeRoleAction?.mode === "edit" && isSelected;

                      return (
                        <Card
                          key={roleKey}
                          sx={{
                            mb: 1,
                            borderRadius: 2,
                            border: isSelected
                              ? "1px solid rgba(255,255,255,0.35)"
                              : "1px solid rgba(255,255,255,0.15)",
                            backdropFilter: "blur(6px)",
                            background: isSelected
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(255,255,255,0.05)",
                            color: "white",
                          }}
                        >
                          <CardContent sx={{ py: 1, px: 1.5, "&:last-child": { pb: 1 } }}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar
                                sx={{
                                  bgcolor: "rgba(255,255,255,0.15)",
                                  width: 30,
                                  height: 30,
                                }}
                              >
                                <BadgeOutlinedIcon sx={{ fontSize: 18 }} />
                              </Avatar>

                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography fontWeight={600} fontSize={13}>
                                  {role.roleName || `Role ${index + 1}`}
                                </Typography>
                              </Box>
                            </Stack>
                          </CardContent>

                          <CardActions
                            sx={{
                              justifyContent: "flex-end",
                              py: 0.5,
                              px: 1.5,
                              pt: 0,
                            }}
                          >
                            <Tooltip title="View">
                              <Button
                                size="small"
                                startIcon={
                                  <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                                }
                                onClick={() => handleRoleAction(role, roleKey, "view")}
                                sx={actionButtonSx}
                                variant={isViewActive ? "contained" : "outlined"}
                              >
                                View
                              </Button>
                            </Tooltip>

                            <Tooltip title="Edit">
                              <Button
                                size="small"
                                startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => handleRoleAction(role, roleKey, "edit")}
                                sx={actionButtonSx}
                                variant={isEditActive ? "contained" : "outlined"}
                              >
                                Edit
                              </Button>
                            </Tooltip>
                          </CardActions>
                        </Card>
                      );
                    })
                  )}
                </Box>

                <Box sx={{ flex: 1 }}>
                  {roleForDetails || loadingDetails ? (
                    <Paper
                      sx={{
                        borderRadius: 2,
                        background: "rgba(255,255,255,0.05)",
                        backdropFilter: "blur(6px)",
                        border: "1px solid rgba(255,255,255,0.15)",
                      }}
                    >
                      <GlobalRoleDetails
                        role={roleForDetails}
                        mode={activeRoleAction?.mode}
                        editForm={editForm}
                        fieldSx={fieldSx}
                        updating={updatingRole}
                        loading={loadingDetails}
                        onEditFormChange={handleEditFormChange}
                        onUpdateRole={handleUpdateRole}
                      />
                    </Paper>
                  ) : (
                    <Typography
                      align="center"
                      fontSize={13}
                      sx={{
                        color: "rgba(255,255,255,0.6)",
                        width: "100%",
                        pt: { xs: 2, md: 0 },
                      }}
                    >
                      Select a global role to view details
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
    </>
  );

  const roleSnackbar = (
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
          {snackbar.message}
        </Alert>
      </Snackbar>
  );

  if (embedded) {
    return (
      <>
        {roleContent}
        {roleSnackbar}
      </>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          flexShrink: 0,
          pl: { xs: 1.5, md: 0 },
          pr: { xs: 1.5, md: 0 },
          ...desktopMainColumnInsetSx,
        }}
      >
        <ModeButtons />
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          justifyContent: "center",
          overflowY: "auto",
          px: { xs: 1.5, sm: 2, md: 3 },
          py: { xs: 2, md: 3 },
          ...desktopMainColumnInsetSx,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 1050,
            p: { xs: 1.5, sm: 2 },
            borderRadius: 2,
            background: "transparent",
            backdropFilter: "blur(6px)",
            border: "1px solid rgba(255,255,255,0.18)",
            color: "white",
            boxSizing: "border-box",
          }}
        >
          {roleContent}
        </Paper>
      </Box>

      {roleSnackbar}
    </Box>
  );
}
